import { AuthProvider } from './hooks/AuthProvider';
import { createTheme } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider as CustomThemeProvider } from './hooks/ThemeProvider';
import { BackdropFilterDetector } from './hooks/BackdropFilterDetector';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import App from './App';
import BrowserRouterWithEffects from './lib/browser_router_with_effects';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import React from 'react';
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import './index.scss';
import { AppSnackbarProvider } from './hooks/SnackbarProvider';
import { GeolocationProvider } from './hooks/GeolocationProvider';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PlanProvider } from './hooks/PlanProvider';
import { Shadows } from '@mui/material/styles/shadows';
import { MapProvider } from './hooks/MapProvider';

declare module '@mui/material/styles' {
  interface Palette {
    custom: Palette['primary'];
  }
  interface PaletteOptions {
    custom?: PaletteOptions['primary'];
  }
  interface Theme {
    gradients: {
      primary: string;
      info: string;
      success: string;
      warning: string;
      error: string;
      sunny: string;
      cloudy: string;
      rainy: string;
      partlyCloudy: string;
    };
  }
  interface ThemeOptions {
    gradients?: {
      primary?: string;
      info?: string;
      success?: string;
      warning?: string;
      error?: string;
      sunny?: string;
      cloudy?: string;
      rainy?: string;
      partlyCloudy?: string;
    };
  }
}


const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, staleTime: 1 * (60 * 1000) } } // Milliseconds
})
const customShadows: Shadows = (() => {
  const shadows: string[] = [];
  for (let elevation = 0; elevation < 25; elevation++) {
    if (elevation === 0) {
      shadows.push('none');
    } else {
      const offsetX = elevation;
      const offsetY = elevation * 2;
      const blur = elevation * 8;
      const alpha = 0.20 + elevation * 0.04;
      const shadow = `${offsetX}px ${offsetY}px ${blur}px 0px rgba(0, 0, 0, ${alpha.toFixed(2)})`;
      shadows.push(shadow);
    }
  }
  return shadows as Shadows;
})();

const theme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: '#F1F3F4', // Page background from mockups
      paper: 'rgba(255, 255, 255, 0.7)' // Glassmorphic base from mockups
    },
    text: {
      primary: '#1F1F1F',
      secondary: '#6B6B6B'
    },
    primary: { main: '#f06ef5' },
    secondary: { main: '#ffc3a0' },
    divider: 'rgba(255, 255, 255, 0.4)', // For glassmorphic borders
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
    h1: { fontSize: '2rem', fontWeight: 600 },
    h2: { fontSize: '1.75rem', fontWeight: 600 },
    h3: { fontSize: '1.5rem', fontWeight: 600 },
    h4: { fontSize: '1.25rem', fontWeight: 600 },
    h5: { fontSize: '1.125rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    body1: { fontSize: '1rem', fontWeight: 400 },
    body2: { fontSize: '0.875rem', fontWeight: 400 },
    caption: { fontSize: '0.75rem', fontWeight: 400 },
  },
  shadows: customShadows,
  gradients: {
    primary: 'linear-gradient(135deg, #ffafbd, #ffc3a0)',
    info: 'linear-gradient(135deg, #91EAE4, #86A8E7)',
    success: 'linear-gradient(135deg, #9be15d, #00e3ae)',
    warning: 'linear-gradient(135deg, #f6d365, #fda085)',
    error: 'linear-gradient(135deg, #f5567b, #fd674c)',
    sunny: 'linear-gradient(135deg, #FFD700, #FFA500)',
    cloudy: 'linear-gradient(135deg, #B0C4DE, #A9A9A9)',
    rainy: 'linear-gradient(135deg, #4682B4, #000080)',
    partlyCloudy: 'linear-gradient(135deg, #87CEEB, #778899)',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 4px rgba(0,0,0,0.3)',
          textTransform: 'none',
          borderRadius: 9999,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          fontWeight: 600,
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0,0,0,0.4)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: 16,
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          overflow: 'hidden',
        }
      }
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#1a1a1a', // Dark mode background from mockups
      paper: 'rgba(28, 28, 30, 0.75)' // Dark mode glassmorphic base
    },
    text: {
      primary: '#F5F5F5',
      secondary: '#AAAAAA'
    },
    primary: { main: '#f06ef5' },
    secondary: { main: '#ffc3a0' },
    divider: 'rgba(255, 255, 255, 0.1)', // Dark mode glassmorphic borders
  },
  shape: { borderRadius: 16 },
  typography: { fontFamily: 'Inter, Roboto, sans-serif' },
  shadows: customShadows,
  gradients: {
    primary: 'linear-gradient(135deg, #ffafbd, #ffc3a0)',
    info: 'linear-gradient(135deg, #91EAE4, #86A8E7)',
    success: 'linear-gradient(135deg, #9be15d, #00e3ae)',
    warning: 'linear-gradient(135deg, #f6d365, #fda085)',
    error: 'linear-gradient(135deg, #f5567b, #fd674c)',
    sunny: 'linear-gradient(135deg, #FFD700, #FFA500)',
    cloudy: 'linear-gradient(135deg, #B0C4DE, #A9A9A9)',
    rainy: 'linear-gradient(135deg, #4682B4, #000080)',
    partlyCloudy: 'linear-gradient(135deg, #87CEEB, #778899)',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: 16,
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
        }
      }
    },
  },
});

// Initialize the Stripe object with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "")

const fontLinks = document.createDocumentFragment();
const links = [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com', crossOrigin: 'anonymous' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap', crossOrigin: 'anonymous' },
  { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap', crossOrigin: 'anonymous' },
  { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Audiowide:wght@300;400;500;700&display=swap', crossOrigin: 'anonymous' },
];

links.forEach(linkProps => {
  const link = document.createElement('link');
  Object.entries(linkProps).forEach(([key, value]) => {
    link.setAttribute(key, value as string);
  });
  fontLinks.appendChild(link);
});

document.head.appendChild(fontLinks);

root.render(
  <React.StrictMode>
    <div className="index-base">
      <header className="index-header">
        <ErrorBoundary>
          <BrowserRouterWithEffects>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || ""}>
              <QueryClientProvider client={queryClient}>
                <AuthProvider>
                  <CustomThemeProvider>
                    {({ isDarkMode }) => (
                      <MuiThemeProvider theme={isDarkMode ? darkTheme : theme}>
                        <BackdropFilterDetector />
                        <MapProvider>
                          <GeolocationProvider>
                            <AppSnackbarProvider>
                              <PlanProvider>
                                <Elements stripe={stripePromise}>
                                  <App />
                                </Elements>
                              </PlanProvider>
                            </AppSnackbarProvider>
                          </GeolocationProvider>
                        </MapProvider>
                      </MuiThemeProvider>
                    )}
                  </CustomThemeProvider>
                </AuthProvider>
                {process.env.NODE_ENV !== 'production' && (<ReactQueryDevtools initialIsOpen={false} />)}
              </QueryClientProvider>
            </GoogleOAuthProvider>
          </BrowserRouterWithEffects>
        </ErrorBoundary>
      </header>
    </div>
  </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
